import { useCallback, useEffect, useMemo, useRef } from 'react'
import { flattenTree } from '../utils/treeUtils'

interface UseTreeSelectShiftClickOptions {
  treeData: any[] | undefined
  fieldNames?: { value?: string; label?: string; children?: string }
  onChange?: (value: any, labelList: any, extra: any) => void
  onOpenChange?: (open: boolean) => void
  treeCheckStrictly?: boolean
  labelInValue?: boolean
}

/**
 * Hook that adds shift+click range selection to Antd TreeSelect checkboxes.
 *
 * Since Antd's onChange doesn't pass the native event (no shiftKey info),
 * this hook tracks shift state via global keydown/keyup listeners and
 * calculates range selection on the data level (not DOM).
 */
export function useTreeSelectShiftClick({
  treeData,
  fieldNames,
  onChange,
  onOpenChange,
  treeCheckStrictly,
  labelInValue
}: UseTreeSelectShiftClickOptions) {
  const shiftHeld = useRef(false)
  const anchor = useRef<string | null>(null)

  // Track shift key state globally
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Shift') shiftHeld.current = true
    }
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') shiftHeld.current = false
    }

    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('keyup', onKeyUp)

    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('keyup', onKeyUp)
    }
  }, [])

  const valueField = fieldNames?.value ?? 'value'
  const labelField = fieldNames?.label ?? 'label'
  const childrenField = fieldNames?.children ?? 'children'

  // Flat list of all node keys in visual (depth-first) order
  const flatKeys = useMemo(
    () => (treeData ? flattenTree(treeData, { valueField, childrenField }) : []),
    [treeData, valueField, childrenField]
  )

  // Normalize value to string array regardless of labelInValue / treeCheckStrictly
  const toStringArray = useCallback(
    (value: any): string[] => {
      if (!Array.isArray(value)) return []
      if (treeCheckStrictly || labelInValue) {
        return value.map((v: any) => String(typeof v === 'object' && v !== null ? v.value : v))
      }
      return value.map((v: any) => String(v))
    },
    [treeCheckStrictly, labelInValue]
  )

  // Build a labeled value object for a given key using treeData lookup
  const buildLabeledValue = useCallback(
    (key: string): { value: string; label: string } => {
      const find = (nodes: any[]): any => {
        for (const node of nodes) {
          if (String(node[valueField]) === key) return node
          if (Array.isArray(node[childrenField])) {
            const found = find(node[childrenField])
            if (found) return found
          }
        }
        return null
      }
      const node = treeData ? find(treeData) : null
      return { value: key, label: node?.[labelField] ?? key }
    },
    [treeData, valueField, labelField, childrenField]
  )

  const wrappedOnChange = useCallback(
    (value: any, labelList: any, extra: any) => {
      const triggerValue = extra?.triggerValue != null ? String(extra.triggerValue) : null
      const isChecking = extra?.checked !== false

      if (!shiftHeld.current || !anchor.current || !triggerValue) {
        // Normal click — update anchor and pass through
        if (triggerValue) {
          anchor.current = triggerValue
        }
        onChange?.(value, labelList, extra)
        return
      }

      // Shift+click — calculate range
      const anchorIdx = flatKeys.indexOf(anchor.current)
      const triggerIdx = flatKeys.indexOf(triggerValue)

      if (anchorIdx === -1 || triggerIdx === -1) {
        // Fallback if keys not found in flat list
        onChange?.(value, labelList, extra)
        return
      }

      const start = Math.min(anchorIdx, triggerIdx)
      const end = Math.max(anchorIdx, triggerIdx)
      const rangeKeys = flatKeys.slice(start, end + 1)

      const currentKeys = toStringArray(value)

      let newKeys: string[]
      if (isChecking) {
        // Union: add range keys to current selection
        const keySet = new Set(currentKeys)
        for (const key of rangeKeys) {
          keySet.add(key)
        }
        newKeys = Array.from(keySet)
      } else {
        // Remove: remove range keys from current selection
        const removeSet = new Set(rangeKeys)
        newKeys = currentKeys.filter(k => !removeSet.has(k))
      }

      // Convert back to the expected format
      let newValue: any
      if (treeCheckStrictly || labelInValue) {
        newValue = newKeys.map(key => buildLabeledValue(key))
      } else {
        newValue = newKeys
      }

      // Update anchor to the trigger value
      anchor.current = triggerValue

      onChange?.(newValue, labelList, extra)
    },
    [onChange, flatKeys, toStringArray, treeCheckStrictly, labelInValue, buildLabeledValue]
  )

  const onDropdownVisibleChange = useCallback(
    (open: boolean) => {
      if (!open) {
        anchor.current = null
      }
      onOpenChange?.(open)
    },
    [onOpenChange]
  )

  return {
    onChange: wrappedOnChange,
    onDropdownVisibleChange
  }
}
